import { AlertTriangle, Check, Eraser, Save, Trash2, Undo2 } from 'lucide-react'
import type { AreaGroup } from '@/types/api'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Field } from '@/components/ui/Field'
import { Input, Select } from '@/components/ui/Input'
import { canClose, closeDraft, undoPoint, type Draft, type DraftCheck } from './polygonDraft'
import styles from './AreaPricingPage.module.css'

interface SubAreaEditorProps {
  /** Null while drawing a new sub-area. */
  editingName: string | null
  draft: Draft
  check: DraftCheck
  name: string
  groupId: number | null
  groups: AreaGroup[]
  saving: boolean
  onDraftChange: (draft: Draft) => void
  onNameChange: (name: string) => void
  onGroupChange: (groupId: number | null) => void
  onSave: () => void
  onCancel: () => void
  onDelete: (() => void) | null
}

const AREA_FORMAT = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 2 })

/**
 * The panel beside the map while a sub-area is drawn or reshaped: how to use
 * the map, what is still wrong with the shape, and its name and group.
 *
 * Save stays disabled until the shape is one the server will accept, and the
 * reasons are listed above it — so the first anyone hears of a crossed edge is
 * while they can still see it, not as an error after clicking.
 */
export function SubAreaEditor({
  editingName,
  draft,
  check,
  name,
  groupId,
  groups,
  saving,
  onDraftChange,
  onNameChange,
  onGroupChange,
  onSave,
  onCancel,
  onDelete,
}: SubAreaEditorProps) {
  const nameMissing = name.trim().length === 0
  const canSave = !saving && !nameMissing && check.problems.length === 0

  return (
    <Card>
      <CardHeader
        title={editingName ? `Edit ${editingName}` : 'New sub-area'}
        description={
          draft.closed
            ? 'Drag a corner to move it, click a small dot to add a corner there, right-click a corner to remove it.'
            : 'Click the map to place each corner. Click the first corner to close the shape.'
        }
      />
      <CardBody>
        <form
          className={styles.panelBody}
          onSubmit={(event) => {
            event.preventDefault()
            if (canSave) onSave()
          }}
        >
          {!draft.closed ? (
            <div className={styles.tools}>
              <Button
                size="sm"
                iconLeft={<Undo2 aria-hidden="true" />}
                onClick={() => onDraftChange(undoPoint(draft))}
                disabled={draft.points.length === 0 || saving}
              >
                Undo corner
              </Button>
              <Button
                size="sm"
                iconLeft={<Check aria-hidden="true" />}
                onClick={() => onDraftChange(closeDraft(draft))}
                disabled={!canClose(draft) || saving}
              >
                Close shape
              </Button>
            </div>
          ) : null}

          <p className={styles.stats} role="status">
            {draft.points.length} {draft.points.length === 1 ? 'corner' : 'corners'}
            {check.areaKm2 !== null && check.areaKm2 > 0 ? ` · about ${AREA_FORMAT.format(check.areaKm2)} km²` : ''}
          </p>

          {check.problems.length > 0 ? (
            <ul className={styles.problems} aria-label="What to fix before saving">
              {check.problems.map((problem) => (
                <li key={problem}>
                  <AlertTriangle aria-hidden="true" />
                  {problem}
                </li>
              ))}
            </ul>
          ) : null}

          <Field label="Name" required hint="As operations call it: Zamalek, Nasr City, Sheikh Zayed.">
            {({ id, describedBy, invalid }) => (
              <Input
                id={id}
                aria-describedby={describedBy}
                invalid={invalid}
                value={name}
                maxLength={100}
                placeholder="Zamalek"
                onChange={(event) => onNameChange(event.target.value)}
                disabled={saving}
              />
            )}
          </Field>

          <Field
            label="Group"
            hint={
              groupId === null
                ? 'In no group, this sub-area adds nothing to a trip.'
                : 'Each end of a trip inside this sub-area pays this group’s surcharge.'
            }
          >
            {({ id, describedBy }) => (
              <Select
                id={id}
                aria-describedby={describedBy}
                value={groupId === null ? '' : String(groupId)}
                onChange={(event) => onGroupChange(event.target.value === '' ? null : Number(event.target.value))}
                disabled={saving}
              >
                <option value="">No group</option>
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                    {group.primary ? ' (primary)' : ''}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <div className={styles.actions}>
            {onDelete ? (
              <Button
                variant="ghost"
                iconLeft={<Trash2 aria-hidden="true" />}
                onClick={onDelete}
                disabled={saving}
              >
                Delete
              </Button>
            ) : (
              <Button
                variant="ghost"
                iconLeft={<Eraser aria-hidden="true" />}
                onClick={() => onDraftChange({ points: [], closed: false })}
                disabled={draft.points.length === 0 || saving}
              >
                Start again
              </Button>
            )}
            <div className={styles.actionsEnd}>
              <Button variant="ghost" onClick={onCancel} disabled={saving}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                iconLeft={<Save aria-hidden="true" />}
                loading={saving}
                disabled={!canSave}
              >
                {editingName ? 'Save changes' : 'Save sub-area'}
              </Button>
            </div>
          </div>
        </form>
      </CardBody>
    </Card>
  )
}
